import React, { useEffect, useState } from "react";
import { Card, Table, Pagination, Form, Alert } from "react-bootstrap";
import styles from "./Scoreboard.module.scss";
import useFetch from "hooks/useFetch";
import ScoreboardSkeleton from "./ScoreboardSkeleton/ScoreboardSkeleton";
import { Button, Header } from "stories";

interface Score {
  username: string;
  points: number;
  questionsAnswered: number;
}

const Scoreboard: React.FC = () => {
  const [topScores, setTopScores] = useState<Score[]>([]);
  const [allScores, setAllScores] = useState<Score[]>([]);
  const [showAllScores, setShowAllScores] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const { execute, isLoading, error } = useFetch<Score[]>();

  const itemsPerPage = 10;
  const maxVisiblePages = 5;

  // Fetch the top 10 scores on initial load
  useEffect(() => {
    const fetchTopScores = async () => {
      const data = await execute("/api/v1/scores/top10", "GET");
      setTopScores(data ?? []);
    };
    fetchTopScores();
  }, []);

  // Fetch all scores when the user opts to view the full scoreboard
  const fetchAllScores = async () => {
    if (allScores.length > 0) {
      setShowAllScores(true); // If already fetched, just display
      return;
    }

    const data = await execute("/api/v1/scores", "GET");
    if (data) {
      setAllScores(data);
      setShowAllScores(true);
    }
  };

  const scoresToDisplay = showAllScores ? allScores : topScores;

  const filteredScores = scoresToDisplay.filter((score) =>
    score.username.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredScores.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredScores.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    if (!showAllScores) return null; // Only show pagination for all scores

    const pages = [];
    const ellipsis = <Pagination.Ellipsis disabled />;

    if (currentPage > 1) {
      pages.push(
        <Pagination.Prev
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Forrige
        </Pagination.Prev>,
      );
    }

    pages.push(
      <Pagination.Item
        key={1}
        active={currentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>,
    );

    if (currentPage > maxVisiblePages) {
      pages.push(ellipsis);
    }

    const startPage = Math.max(
      2,
      currentPage - Math.floor(maxVisiblePages / 2),
    );
    const endPage = Math.min(
      totalPages - 1,
      currentPage + Math.floor(maxVisiblePages / 2),
    );

    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <Pagination.Item
          key={page}
          active={currentPage === page}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>,
      );
    }

    if (currentPage < totalPages - maxVisiblePages + 1) {
      pages.push(ellipsis);
    }

    if (totalPages > 1) {
      pages.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    if (currentPage < totalPages) {
      pages.push(
        <Pagination.Next
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Neste
        </Pagination.Next>,
      );
    }

    return <Pagination className="justify-content-center">{pages}</Pagination>;
  };

  if (isLoading) {
    return <ScoreboardSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center mt-4 text-danger">
        <p>Error: {error.text || "Failed to load scores."}</p>
      </div>
    );
  }

  return (
    <div className={styles.scoreboardContainer}>
      <Header title="Poengliste" />
      <br />
      <Card className={styles.scoreboardCard}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Form.Control
              type="text"
              placeholder="Søk etter brukernavn..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-50"
            />
          </div>
          {filteredScores.length === 0 ? (
            <Alert variant="warning" className="text-center">
              Ingen brukere funnet med søkekriteriene. Vennligst prøv et annet
              søkeord.
            </Alert>
          ) : (
            <>
              <Table
                bordered
                hover
                responsive
                className={styles.scoreboardTable}
              >
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Brukernavn</th>
                    <th>Poeng</th>
                    <th>Besvarte Spørsmål</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((score, index) => (
                    <tr key={score.username}>
                      <td>
                        {showAllScores
                          ? indexOfFirstItem + index + 1
                          : index + 1}
                      </td>
                      <td>{score.username}</td>
                      <td>{score.points}</td>
                      <td>{score.questionsAnswered}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {renderPagination()}
            </>
          )}
          <div className="text-center mt-4">
            {!showAllScores && (
              <Button
                variant="secondary"
                onClick={fetchAllScores}
                size="large"
                label="Vis Alle"
              />
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Scoreboard;
